import { FiasAddress } from 'Models'
import { FieldNames } from 'Constants/FieldNames'
import {
	TYPE_ORGANIZATION,
	SPOD_STATUS,
	SPOD_PARAMS,
	InfotecsData,
	RbidosRequest,
	UserDocs,
	FREE_RBIDOS_REQUEST,
	PAID_STATUS,
	allFormFields,
	ClientType,
	passportDataFields,
	TAXATION_OPTIONS_OOO,
	TAXATION_OPTIONS_IP,
	TaxationOptions,
} from 'Pages/registratorCabinet'
import { EXTENDED_REQUIREMENTS_REGIONS } from 'Constants/Global'
import { WrappedFormUtils } from 'antd/lib/form/Form'
import translate_errors from 'Src/locale/ru/errors.json'
import { intersection, union } from 'lodash'

export const getExternalErrors = (externalErrorsArray: string[]) => {
	return externalErrorsArray.map((current) => current.substr(current.lastIndexOf('.') + 1))
}

export const getCallbackErrors = (errors) => {
	return errors.map((err) => err.message.toLowerCase()).join(', ')
}

export const fieldHasDocuments = (obj: UserDocs, fieldName: FieldNames) =>
	Boolean(obj?.[fieldName]?.documents?.length)

export const isIncludeRegion = (address: FiasAddress) =>
	Boolean(EXTENDED_REQUIREMENTS_REGIONS.includes(address?.regionCode))

export const isLoadDocumentPassport = (obj: RbidosRequest) => {
	return Boolean(
		fieldHasDocuments(obj?.[FieldNames.USER_DOCS], FieldNames.USER_PASSPORT_MAINS) ||
			fieldHasDocuments(obj?.[FieldNames.USER_DOCS], FieldNames.USER_PASSPORT_REGISTRATION) ||
			fieldHasDocuments(obj?.[FieldNames.USER_DOCS], FieldNames.USER_PASSPORT_FULLS)
	)
}

export const getPassportSeries = (series: string) => {
	if (series?.length === 4) {
		return `${series.substring(0, 2)} ${series.substring(2, 4)}`
	}
	return series
}

export const getPassportGivenDept = (dept: string) => {
	if (dept?.length === 6) {
		return `${dept.substring(0, 3)}-${dept.substring(3, 6)}`
	}
	return dept
}

export const getSnils = (snils: string) => {
	if (snils?.length === 11) {
		return `${snils.substring(0, 3)}-${snils.substring(3, 6)}-${snils.substring(
			6,
			9
		)}-${snils.substring(9, 11)}`
	}
	return snils
}

export const changeFullPassport = (
	address: FiasAddress,
	order: RbidosRequest,
	form: WrappedFormUtils
) => {
	const { setFieldsValue } = form
	if (!isLoadDocumentPassport(order)) {
		setFieldsValue({
			[FieldNames.PASSPORT_FULL]: isIncludeRegion(address),
		})
	}
}

export const concatFullName = ({
	last_name,
	first_name,
	middle_name,
}: Partial<
	Record<FieldNames.LAST_NAME | FieldNames.FIRST_NAME | FieldNames.MIDDLE_NAME, string | null>
>): string | null => {
	return (
		`${last_name || ''}${first_name ? ` ${first_name}` : ''}${
			middle_name ? ` ${middle_name}` : ''
		}` || null
	)
}

export const isOOOOrgType = (org_type: TYPE_ORGANIZATION) => org_type === TYPE_ORGANIZATION.OOO

export const switchValueTranslate = (value: boolean) => (value ? 'Да' : 'Нет')

export const isAvailableDataInFields = (data: FieldNames[], item: RbidosRequest) =>
	data.some(
		(fieldName) => item[fieldName] || (Array.isArray(item[fieldName]) && item[fieldName].length)
	)

export const getResultSpod = (spod_data): SPOD_STATUS => {
	if (spod_data?.spod_status === SPOD_STATUS.PROCESSING) return SPOD_STATUS.PROCESSING
	if (
		spod_data?.spod_status === SPOD_STATUS.SUCCESS &&
		spod_data?.spod_result?.cert > SPOD_PARAMS.CERT &&
		spod_data?.spod_result?.signs === SPOD_PARAMS.SIGNS
	)
		return SPOD_STATUS.SUCCESS
	if (
		spod_data?.spod_status === SPOD_STATUS.SUCCESS &&
		(spod_data?.spod_result?.cert <= SPOD_PARAMS.CERT ||
			spod_data?.spod_result?.signs !== SPOD_PARAMS.SIGNS)
	)
		return SPOD_STATUS.FAIL
	return SPOD_STATUS.UNKNOW
}

export const InfotecsDocumentNames = {
	0: 'Разворот паспорта с фото',
	1: 'Фото клиента с сертификатом',
	2: 'Скан подписанного сертификата',
}

export const getResultDocumentsSpod = (documents: InfotecsData['documents']): SPOD_STATUS =>
	getResultSpod(
		documents.find((document): boolean =>
			[SPOD_STATUS.PROCESSING, SPOD_STATUS.SUCCESS].includes(document.spod_data?.spod_status)
		)?.spod_data
	)

export const hasPaymentPkr = (
	order: Pick<RbidosRequest, 'rbidos_id' | 'paid_status'>,
	freeRbidosRequest: string[]
) =>
	(!order?.rbidos_id && !freeRbidosRequest.includes(FREE_RBIDOS_REQUEST.PKR)) ||
	order?.paid_status === PAID_STATUS.IS_PAID

export const addressErrorsHandler = (error, data: RbidosRequest) => {
	error.data.errors = error?.data?.errors.filter((error) => {
		return data.org_type === TYPE_ORGANIZATION.IP
			? !error[FieldNames.JURIDICAL_ADDRESS] && !error[FieldNames.POSTAL_ADDRESS]
			: !Object.keys(error)[0].includes(FieldNames.POSADDRESS)
	})

	return error
}

export const formErrorsHandler = (error) => {
	error.data.errors = error.data.errors?.reduce((acc, val) => {
		if (allFormFields.includes(Object.keys(val)?.[0] as FieldNames)) {
			if (!acc.find((key) => key.custom_error)) {
				acc = [...acc, { custom_error: 'form_errors' }]
			}
		} else acc = [...acc, val]
		return acc
	}, [])
	return error
}

export const getTranslateErrors = (errors: string[]): string => {
	const translate = errors.map((error) => translate_errors[error])
	return translate.join(', ')
}

export const isLockedField = (order: RbidosRequest, fieldName: FieldNames) =>
	order?.locked_fields?.includes(fieldName)

export const getNameRegion = (data: any): string => {
	const isIP = data?.[FieldNames.ORG_TYPE] === TYPE_ORGANIZATION.IP
	const address = isIP ? data?.[FieldNames.ADDRESS] : data?.[FieldNames.JURIDICAL_ADDRESS]
	return isIncludeRegion(address)
		? `Ваш клиент зарегистрирован в регионе: "${address?.region} ${address?.region_type_full}". `
		: null
}

// TODO: duplicate function in project
export const getClientTypeByOrgType = (orgType?: string) => {
	if (!orgType) return ClientType.ALL
	return orgType === TYPE_ORGANIZATION.IP ? ClientType.IP : ClientType.COMPANY
}

export const viewOnlineField = (formValues: any): boolean =>
	Boolean(
		formValues?.[FieldNames.PRODUCT_IDS] &&
			(formValues?.[FieldNames.ONLINE_PRODUCT_IDS] ||
				formValues?.[FieldNames.ONLINE_MANAGER_PRODUCT_IDS]) &&
			intersection(
				formValues[FieldNames.PRODUCT_IDS],
				union(
					formValues[FieldNames.ONLINE_PRODUCT_IDS],
					formValues?.[FieldNames.ONLINE_MANAGER_PRODUCT_IDS]
				)
			)?.length
	)

export const isAllowed = (limit) => (values) => {
	const { floatValue } = values
	return !floatValue || floatValue < limit
}

export const isEmpty = (form: WrappedFormUtils) => {
	return passportDataFields.some((field) => {
		const value = form?.getFieldValue(field)
		return !value
	})
}

export const getTaxationOptions = (OOOOrgType: boolean, hasAllowedRole: boolean) => {
	const options = OOOOrgType ? TAXATION_OPTIONS_OOO : TAXATION_OPTIONS_IP
	return options.filter((option) => {
		if (option.key === TaxationOptions.ESHN) {
			return hasAllowedRole
		}
		return true
	})
}
